import { useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Search, AlertTriangle, Loader2, AlertCircle, Power, CheckCircle2, X } from 'lucide-react';
import { cutoverApi } from '../../services/cutoverApi';
import { clinicApi } from '@features/clinics/services/clinicApi';
import { ClinicStatus } from '@features/clinics/types/clinic';
import { getApiErrorMessage } from '@shared/utils/apiError';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import PreviewTable from './PreviewTable';
import type { ColumnDef } from './PreviewTable';
import BulkOperationResultPanel from './BulkOperationResultPanel';
import type {
  ClinicDeactivationPreviewDto,
  ClinicDeactivationMatchDto,
  BulkOperationResultDto,
} from '../../types/cutover';
import './ClinicDeactivationTab.css';

const PRESET_WORDS = ['Do Not Use', 'Closed', 'DELETED', 'Test'];

// Active/inactive is derived from the SAME field the Status column renders.
// The API now sends the *effective* status — Clinics.Status reconciled with
// Items.Active (SLV2-3195) — so currentStatus is reliable even for legacy rows
// where the raw int column was unmaintained. Anything not Inactive can be
// deactivated; Inactive rows offer Activate.
const isMatchActive = (m: ClinicDeactivationMatchDto): boolean =>
  m.currentStatus !== ClinicStatus.Inactive;

export default function ClinicDeactivationTab() {
  // State
  const [triggerWords, setTriggerWords] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [caseInsensitive, setCaseInsensitive] = useState(true);
  const [scanResult, setScanResult] = useState<ClinicDeactivationPreviewDto | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deactivateResult, setDeactivateResult] = useState<BulkOperationResultDto | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Parse trigger words from textarea
  function parseTriggerWords(): string[] {
    return triggerWords
      .split('\n')
      .map((w) => w.trim())
      .filter((w) => w.length > 0);
  }

  // Status-aware partition of the current selection: only Active rows can be
  // deactivated, only non-Active rows can be activated. The bulk buttons use
  // these so each action applies to (and counts) just the relevant rows.
  const selectedMatches = useMemo(
    () => (scanResult ? scanResult.matches.filter((m) => selectedIds.has(m.clinicId)) : []),
    [scanResult, selectedIds],
  );
  const selectedActiveIds = useMemo(
    () => selectedMatches.filter(isMatchActive).map((m) => m.clinicId),
    [selectedMatches],
  );
  const selectedInactiveIds = useMemo(
    () => selectedMatches.filter((m) => !isMatchActive(m)).map((m) => m.clinicId),
    [selectedMatches],
  );

  // Mutations
  const scanMutation = useMutation({
    mutationFn: () => {
      const words = parseTriggerWords();
      if (words.length === 0) throw new Error('Enter at least one trigger word');
      return cutoverApi.scanForDeactivation({
        triggerWords: words,
        caseInsensitive,
        activeOnly,
      });
    },
    onSuccess: (result) => {
      setScanResult(result);
      setSelectedIds(new Set(result.matches.map((m) => m.clinicId)));
      setDeactivateResult(null);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => {
      if (selectedActiveIds.length === 0) throw new Error('No active clinics selected');
      return cutoverApi.executeDeactivation({
        clinicIds: selectedActiveIds,
        triggerWords: parseTriggerWords(),
      });
    },
    onSuccess: (result) => {
      setDeactivateResult(result);
      setShowConfirmModal(false);
      setScanResult(null);
    },
  });

  // Individual + bulk status change (Activate/Deactivate) via the per-clinic
  // status endpoint. Works for one clinic (row action) or many (bulk).
  const [statusAction, setStatusAction] = useState<{ text: string; isError: boolean } | null>(null);
  const statusChangeMutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: number[]; status: number }) => {
      if (ids.length === 0) throw new Error('No clinics selected');
      const results = await Promise.allSettled(ids.map((id) => clinicApi.updateStatus(id, status)));
      const rejected = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      const firstError = rejected.length > 0 ? getApiErrorMessage(rejected[0].reason) : null;
      return { total: ids.length, failed: rejected.length, status, firstError };
    },
    onSuccess: ({ total, failed, status, firstError }) => {
      const verb = status === ClinicStatus.Active ? 'activated' : 'deactivated';
      const ok = total - failed;
      setStatusAction({
        text:
          `${ok} of ${total} clinic${total !== 1 ? 's' : ''} ${verb}` +
          (failed ? ` — ${failed} failed: ${firstError}` : '.'),
        isError: failed > 0,
      });
      // Refresh the scan so the table reflects new statuses.
      if (parseTriggerWords().length > 0) scanMutation.mutate();
    },
    onError: (err: unknown) => {
      setStatusAction({ text: getApiErrorMessage(err), isError: true });
    },
  });

  const changeStatus = (ids: number[], status: number) => {
    setStatusAction(null);
    statusChangeMutation.mutate({ ids, status });
  };

  // Preset button handler
  function handleAddPreset(word: string) {
    const lines = triggerWords
      .split('\n')
      .map((w) => w.trim())
      .filter((w) => w.length > 0);
    if (!lines.some((l) => l.toLowerCase() === word.toLowerCase())) {
      const updated = [...lines, word].join('\n');
      setTriggerWords(updated);
    }
  }

  // Table columns
  const columns = useMemo<ColumnDef<ClinicDeactivationMatchDto>[]>(
    () => [
      {
        key: 'clinicName',
        header: 'Clinic Name',
        sortable: true,
      },
      {
        key: 'code',
        header: 'Code',
        sortable: true,
        width: '80px',
      },
      {
        key: 'city',
        header: 'Location',
        sortable: true,
        render: (_val, row) => {
          if (!row.city && !row.state) return '—';
          return [row.city, row.state].filter(Boolean).join(', ');
        },
      },
      {
        key: 'currentStatusDisplay',
        header: 'Status',
        sortable: true,
        width: '100px',
        render: (val) => (
          <span className="cdt-status-badge">{String(val ?? 'Unknown')}</span>
        ),
      },
      {
        key: 'matchedTriggerWord',
        header: 'Matched Word',
        sortable: true,
        render: (val) => (
          <span className="cdt-trigger-badge">{String(val ?? '')}</span>
        ),
      },
      {
        key: 'isHierarchyParent',
        header: 'Warnings',
        width: '140px',
        render: (_val, row) =>
          row.isHierarchyParent ? (
            <span
              className="cdt-hierarchy-warning"
              title={`Parent of ${row.childCount} child clinic(s)`}
            >
              <AlertTriangle size={14} />
              <span>Parent ({row.childCount})</span>
            </span>
          ) : (
            '—'
          ),
      },
      {
        key: 'clinicId',
        header: 'Action',
        width: '130px',
        render: (_val, row) => {
          const isActive = isMatchActive(row);
          return (
            <button
              type="button"
              className={`cdt-row-action ${isActive ? 'cdt-row-action--deactivate' : 'cdt-row-action--activate'}`}
              onClick={() =>
                changeStatus([row.clinicId], isActive ? ClinicStatus.Inactive : ClinicStatus.Active)
              }
              disabled={statusChangeMutation.isPending}
            >
              <Power size={13} />
              {isActive ? 'Deactivate' : 'Activate'}
            </button>
          );
        },
      },
    ],
    // changeStatus only calls referentially-stable fns; re-memo on pending to
    // keep the row buttons' disabled state in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusChangeMutation.isPending],
  );

  // Computed — only Active rows are actually deactivated, so the hierarchy-parent
  // warning is based on the active subset.
  const activeIdSet = new Set(selectedActiveIds);
  const selectedParentCount = scanResult
    ? scanResult.matches.filter((m) => activeIdSet.has(m.clinicId) && m.isHierarchyParent).length
    : 0;

  const isAnyLoading = scanMutation.isPending || deactivateMutation.isPending;
  const error = scanMutation.error || deactivateMutation.error;

  return (
    <div className="cdt">
      {/* Trigger Words Input */}
      <div className="cdt-input-section">
        <div className="cdt-input-row">
          <div className="cdt-textarea-wrapper">
            <label htmlFor="trigger-words" className="cdt-label">
              Trigger Words
            </label>
            <textarea
              id="trigger-words"
              className="cdt-textarea"
              placeholder="Enter one trigger word or phrase per line..."
              value={triggerWords}
              onChange={(e) => setTriggerWords(e.target.value)}
              rows={5}
            />
          </div>
          <div className="cdt-presets">
            <span className="cdt-presets-label">Presets:</span>
            <div className="cdt-preset-buttons">
              {PRESET_WORDS.map((word) => (
                <button
                  key={word}
                  type="button"
                  className="cdt-preset-btn"
                  onClick={() => handleAddPreset(word)}
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Options Row */}
        <div className="cdt-options-row">
          <label className="cdt-checkbox-label">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            Active only
          </label>
          <label className="cdt-checkbox-label">
            <input
              type="checkbox"
              checked={caseInsensitive}
              onChange={(e) => setCaseInsensitive(e.target.checked)}
            />
            Case insensitive
          </label>
          <div className="cdt-options-spacer" />
          <button
            type="button"
            className="cdt-btn cdt-btn-primary"
            onClick={() => scanMutation.mutate()}
            disabled={isAnyLoading || parseTriggerWords().length === 0}
          >
            {scanMutation.isPending ? (
              <Loader2 size={14} className="cdt-spin" />
            ) : (
              <Search size={14} />
            )}
            Scan
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="cdt-error">
          <AlertCircle size={16} />
          <span>{String(error)}</span>
        </div>
      )}

      {/* Scan Results */}
      {scanResult && !deactivateResult && (
        <div className="cdt-results-section">
          <div className="cdt-results-header">
            <h3>{scanResult.totalMatches} match{scanResult.totalMatches !== 1 ? 'es' : ''} found</h3>
            <span className="cdt-trigger-list">
              Trigger words: {scanResult.triggerWordsUsed.join(', ')}
            </span>
          </div>

          {/* Action result/error — shown at the top so it's visible for both
              single row actions and bulk actions, regardless of scroll. */}
          {statusAction && (
            <div
              className={`cdt-action-banner ${statusAction.isError ? 'cdt-action-banner--error' : 'cdt-action-banner--success'}`}
              role={statusAction.isError ? 'alert' : 'status'}
            >
              {statusAction.isError ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
              <span>{statusAction.text}</span>
              <button
                type="button"
                className="cdt-action-banner-dismiss"
                onClick={() => setStatusAction(null)}
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )}

          <PreviewTable<ClinicDeactivationMatchDto>
            data={scanResult.matches}
            columns={columns}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            idField="clinicId"
            emptyMessage="No clinics matched the trigger words"
          />

          {/* Action Bar: bulk activate / deactivate selected */}
          {scanResult.matches.length > 0 && (
            <div className="cdt-action-bar">
              <button
                type="button"
                className="cdt-btn cdt-btn-success"
                onClick={() => changeStatus(selectedInactiveIds, ClinicStatus.Active)}
                disabled={selectedInactiveIds.length === 0 || isAnyLoading || statusChangeMutation.isPending}
                title="Activates only the selected rows that are currently inactive"
              >
                {statusChangeMutation.isPending ? (
                  <Loader2 size={14} className="cdt-spin" />
                ) : (
                  <CheckCircle2 size={14} />
                )}
                Activate Selected ({selectedInactiveIds.length})
              </button>
              <button
                type="button"
                className="cdt-btn cdt-btn-danger"
                onClick={() => setShowConfirmModal(true)}
                disabled={selectedActiveIds.length === 0 || isAnyLoading || statusChangeMutation.isPending}
                title="Deactivates only the selected rows that are currently active"
              >
                {deactivateMutation.isPending ? (
                  <Loader2 size={14} className="cdt-spin" />
                ) : null}
                Deactivate Selected ({selectedActiveIds.length})
              </button>
            </div>
          )}
        </div>
      )}

      {/* Deactivation Results */}
      {deactivateResult && (
        <div className="cdt-final-results">
          <BulkOperationResultPanel result={deactivateResult} title="Deactivation Complete" />
          <div className="cdt-final-actions">
            <button
              type="button"
              className="cdt-btn cdt-btn-secondary"
              onClick={() => {
                setDeactivateResult(null);
                setScanResult(null);
                setSelectedIds(new Set());
              }}
            >
              Start Over
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        title="Deactivate Clinics"
        message={
          `Deactivate ${selectedActiveIds.length} active clinic${selectedActiveIds.length !== 1 ? 's' : ''}?` +
          (selectedParentCount > 0
            ? `\n\nWarning: ${selectedParentCount} of these clinic${selectedParentCount !== 1 ? 's are' : ' is a'} hierarchy parent${selectedParentCount !== 1 ? 's' : ''}. Their child clinics will become orphaned.`
            : '') +
          '\n\nThis action sets Status to Inactive and cannot be easily undone.'
        }
        confirmText="Deactivate"
        cancelText="Cancel"
        onConfirm={() => deactivateMutation.mutate()}
        onCancel={() => setShowConfirmModal(false)}
        danger
        loading={deactivateMutation.isPending}
      />
    </div>
  );
}